import type { Grid, Token, Direction, Pos } from "../types.ts";
import { applyDirection } from "./movement.ts";
import { cloneTokens } from "./grid.ts";

const DIRECTIONS: Direction[] = ["up", "down", "left", "right"];
const MAX_STATES = 1_000_000;

function stateKey(tokens: Token[]): string {
  // Canonical key: positions sorted by color then position for consistency
  const sorted = [...tokens].sort((a, b) => {
    if (a.color < b.color) return -1;
    if (a.color > b.color) return 1;
    if (a.pos.row !== b.pos.row) return a.pos.row - b.pos.row;
    return a.pos.col - b.pos.col;
  });
  return sorted.map((t) => (t.lost ? "X" : `${t.pos.row},${t.pos.col}`)).join("|");
}

interface BfsEntry {
  key: string;
  tokens: Token[];
  depth: number;
}

export function solve(
  grid: Grid,
  initialTokens: Token[],
  maxDepth: number
): Direction[] | null {
  const tokens = cloneTokens(initialTokens);
  const startKey = stateKey(tokens);

  const visited = new Map<string, { parent: string | null; move: Direction | null }>();
  visited.set(startKey, { parent: null, move: null });

  const queue: BfsEntry[] = [{ key: startKey, tokens, depth: 0 }];
  let queueIdx = 0;

  while (queueIdx < queue.length) {
    const entry = queue[queueIdx++];

    if (entry.depth >= maxDepth) continue;

    // Bail if state space is too large
    if (visited.size >= MAX_STATES) return null;

    for (const dir of DIRECTIONS) {
      const result = applyDirection(grid, entry.tokens, dir);

      // Skip if any token lost
      if (result.anyLost) continue;

      const newKey = stateKey(result.tokens);

      // Skip if already visited
      if (visited.has(newKey)) continue;

      visited.set(newKey, { parent: entry.key, move: dir });

      if (result.won) {
        return reconstructPath(visited, newKey);
      }

      queue.push({ key: newKey, tokens: result.tokens, depth: entry.depth + 1 });
    }
  }

  return null;
}

function reconstructPath(
  visited: Map<string, { parent: string | null; move: Direction | null }>,
  endKey: string
): Direction[] {
  const path: Direction[] = [];
  let current = endKey;

  while (true) {
    const entry = visited.get(current);
    if (!entry || entry.move === null) break;
    path.unshift(entry.move);
    current = entry.parent!;
  }

  return path;
}
